import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useAuthRedirect } from '../utils/authRedirect';
import AccountPanel from './AccountPanel';
import RemoveAdsPayment from './RemoveAdsPayment';
import { stripePromise } from '../utils/stripe';

function Profile({ isDarkMode, toggleDarkMode, onLogout }) {
    const [hasNoAdsState, setHasNoAdsState] = useState(null);
    const [paymentClientSecret, setPaymentClientSecret] = useState(null);
    const [paymentIntentId, setPaymentIntentId] = useState(null);
    const [showPaymentSheet, setShowPaymentSheet] = useState(false);
    const [isCreatingPayment, setIsCreatingPayment] = useState(false);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const [error, setError] = useState('');
    const [banner, setBanner] = useState('');

    const REMOVE_ADS_AMOUNT = 500;
    const REMOVE_ADS_CURRENCY = 'usd';
    const REFERRAL_DISCOUNT_PERCENT = 50;

    const handleAuthError = useAuthRedirect();
    const { user, loading: userLoading, refreshUser, updateUser } = useUser();
    const getToken = () => localStorage.getItem('token');

    useEffect(() => {
        if (userLoading) {
            return;
        }

        if (user) {
            setHasNoAdsState(typeof user.hasNoAds === 'boolean' ? user.hasNoAds : false);
        } else {
            setHasNoAdsState(null);
        }
    }, [user, userLoading]);

    const discountedRemoveAdsAmount = user?.hasReferralDiscount
        ? Math.round(REMOVE_ADS_AMOUNT * (100 - REFERRAL_DISCOUNT_PERCENT) / 100)
        : REMOVE_ADS_AMOUNT;
    const formattedRemoveAdsPrice = `$${(discountedRemoveAdsAmount / 100).toFixed(2)}`;
    const currentPlanLabel = hasNoAdsState ? 'Ad-free' : 'Free';
    const adsStateLabel = hasNoAdsState ? 'Disabled' : 'Enabled';
    const billingTypeLabel = hasNoAdsState ? 'One-time lifetime unlock' : 'One-time upgrade available';

    const handlePaymentPanelClose = useCallback(() => {
        setShowPaymentSheet(false);
        setPaymentClientSecret(null);
        setPaymentIntentId(null);
        setIsProcessingPayment(false);
        setIsCheckoutLoading(false);
    }, []);

    const handleRemoveAds = async () => {
        try {
            setError('');
            setBanner('');
            setIsCreatingPayment(true);
            const response = await fetch('/api/payment/create-payment-intent', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': getToken(),
                },
            });

            if (handleAuthError(response.status)) {
                return;
            }

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to start payment.');
            }

            const data = await response.json();
            setPaymentClientSecret(data.clientSecret);
            setPaymentIntentId(data.paymentIntentId);
            setShowPaymentSheet(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsCreatingPayment(false);
        }
    };

    const handleCheckoutFallback = useCallback(async () => {
        try {
            setError('');
            setIsCheckoutLoading(true);
            const response = await fetch('/api/payment/create-checkout-session', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': getToken(),
                },
            });

            if (handleAuthError(response.status)) {
                return;
            }

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to create checkout session.');
            }

            const session = await response.json();
            if (session.url) {
                window.location.href = session.url;
                return;
            }

            const publicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
            if (!publicKey) {
                throw new Error('Stripe public key is not configured.');
            }

            const resolvedStripe = await stripePromise;
            if (!resolvedStripe) {
                throw new Error('Stripe.js failed to load.');
            }

            const result = await resolvedStripe.redirectToCheckout({
                sessionId: session.id,
            });

            if (result?.error) {
                throw new Error(result.error.message);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsCheckoutLoading(false);
        }
    }, [handleAuthError]);

    const handlePaymentProcessingChange = useCallback((state) => {
        setIsProcessingPayment(state);
    }, []);

    const handlePaymentComplete = useCallback(async () => {
        let message = 'Payment successful! Ads have been removed.';
        try {
            if (paymentIntentId) {
                const resp = await fetch('/api/payment/confirm-intent', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-auth-token': getToken(),
                    },
                    body: JSON.stringify({ paymentIntentId }),
                });

                if (handleAuthError(resp.status)) {
                    return;
                }

                if (resp.ok) {
                    const result = await resp.json();
                    if (typeof result.hasNoAds === 'boolean') {
                        setHasNoAdsState(result.hasNoAds);
                        updateUser({ hasNoAds: result.hasNoAds });
                        if (!result.hasNoAds) {
                            message = 'Payment succeeded. Ads will be removed shortly.';
                        }
                    }
                } else if (resp.status === 202) {
                    message = 'Payment is processing. Ads will disappear once Stripe confirms the charge.';
                } else {
                    const errData = await resp.json().catch(() => ({}));
                    message = errData.error
                        ? `Payment verified, but we could not update your profile automatically: ${errData.error}`
                        : 'Payment verified, but we could not update your profile automatically.';
                }
            }
        } catch (err) {
            message = `Payment received, but verification failed. Please refresh in a moment. (${err.message})`;
        }

        await refreshUser();
        setBanner(message);
        handlePaymentPanelClose();
    }, [paymentIntentId, refreshUser, handleAuthError, handlePaymentPanelClose, updateUser]);

    const isLoading = userLoading;

    return (
        <div className="page profile-page">
            <header className="page-header">
                <span className="page-eyebrow">Profile</span>
                <h1>Account settings</h1>
                <p className="page-subtitle">
                    Keep your account tidy and your cooking workflow uninterrupted.
                </p>
                <div className="profile-actions">
                    <Link to="/saved-recipes" className="btn btn-outline-secondary">
                        Saved recipes
                    </Link>
                    <Link to="/app" className="btn btn-primary">
                        Workspace
                    </Link>
                </div>
            </header>

            {error && <div className="alert alert-danger" role="alert">{error}</div>}
            {banner && <div className="alert alert-info" role="alert">{banner}</div>}

            {isLoading ? (
                <p>Loading...</p>
            ) : (
                <div className="profile-layout">
                    <aside className="profile-sidebar">
                        <AccountPanel
                            user={user}
                            hasNoAdsState={hasNoAdsState}
                            onLogout={onLogout}
                        />

                        <div className="panel profile-panel">
                            <h5>Theme preference</h5>
                            <div className="form-check form-switch mt-3">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="darkModeSwitch"
                                    checked={isDarkMode}
                                    onChange={toggleDarkMode}
                                />
                                <label className="form-check-label" htmlFor="darkModeSwitch">
                                    {isDarkMode ? 'Dark mode' : 'Light mode'}
                                </label>
                            </div>
                        </div>

                    </aside>

                    <main className="profile-content">
                        <div className="panel profile-panel">
                            <div className="plan-billing-header">
                                <div>
                                    <h5>Plan and billing</h5>
                                    <p className="text-muted mb-0">
                                        Understand your current plan and choose your next billing action.
                                    </p>
                                </div>
                                <span className={`badge ${hasNoAdsState ? 'bg-success' : 'bg-warning text-dark'}`}>
                                    {hasNoAdsState ? 'Ad-free' : 'Ads active'}
                                </span>
                            </div>

                            <div className="plan-current-state">
                                <span className="account-stat__label">Current plan</span>
                                <h6>{currentPlanLabel} plan • Ads {adsStateLabel.toLowerCase()}</h6>
                                <p className="text-muted mb-0">Billing: {billingTypeLabel}.</p>
                            </div>

                            <div className="plan-billing-detail">
                                <span className="account-stat__label">Billing type</span>
                                <span className="account-stat__value">{billingTypeLabel}</span>
                            </div>

                            {user?.hasReferralDiscount && (
                                <p className="text-success small mb-0">
                                    Referral discount active: {REFERRAL_DISCOUNT_PERCENT}% off. Your price is {formattedRemoveAdsPrice}.
                                </p>
                            )}

                            {hasNoAdsState === false && (
                                <div className="plan-billing-actions">
                                    <button
                                        onClick={handleRemoveAds}
                                        className="btn btn-primary btn-lg"
                                        disabled={hasNoAdsState !== false || isCreatingPayment || isProcessingPayment || showPaymentSheet}
                                    >
                                        {isCreatingPayment || isProcessingPayment ? 'Preparing payment...' : `Remove ads forever (${formattedRemoveAdsPrice})`}
                                    </button>
                                    <p className="text-muted small mb-0">
                                        One-time payment. No recurring subscription. Wallets like Apple Pay and Google Pay are supported when available.
                                    </p>
                                </div>
                            )}
                            {hasNoAdsState === true && (
                                <p className="text-success small mb-0">
                                    You already have lifetime ad-free access on this account.
                                </p>
                            )}

                            {showPaymentSheet && paymentClientSecret && hasNoAdsState === false && (
                                <div className="mt-4">
                                    <RemoveAdsPayment
                                        clientSecret={paymentClientSecret}
                                        amount={discountedRemoveAdsAmount}
                                        currency={REMOVE_ADS_CURRENCY}
                                        onComplete={handlePaymentComplete}
                                        onCancel={handlePaymentPanelClose}
                                        onProcessingChange={handlePaymentProcessingChange}
                                        onCheckoutFallback={handleCheckoutFallback}
                                        isCheckoutLoading={isCheckoutLoading}
                                        isDarkMode={isDarkMode}
                                    />
                                </div>
                            )}
                        </div>
                    </main>
                </div>
            )}
        </div>
    );
}

export default Profile;
